import { yupResolver } from '@hookform/resolvers/yup';
import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useSurveyPermissions } from '../../../../models/hooks/useSurveyPermissions';
import { copyToClipboard } from '../../../../models/UiUtils';
import {
	AppDrawer,
	AppDrawerToolbar,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../../components-v2/Drawer/AppDrawer';
import { Page } from '../../../components-v2/PageLayout';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { FabContext } from '../../../components/FabContext';
import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { QRCodeModal } from '../../../components/QRCodeModal';
import { UnsavedChangesWarning } from '../shared/components/UnsavedChangesWarning';
import { useUnsavedChanges } from '../shared/hooks/useUnsavedChanges';
import { hasCustomFormChanges } from '../shared/utils/customFormComparison';
import { EyeIcon } from '../../../components/svgs/icons/EyeIcon';
import { ListOfQuestionsIcon } from '../../../components/svgs/icons/ListOfQuestionsIcon';
import { TabSettingsIcon } from '../../../components/svgs/icons/TabSettingsIcon';
import { IconTabTrigger, TabContent, TabList, TabRoot } from '../../../components/Tabs';
import { useClonableSurvey } from '../../../hooks/useClonableSurvey';
import { DefaultUniversalAssistantChat } from '../../templates/AssistantChat';
import { AdditionalQuestions } from '../shared/components/AdditionalQuestions';
import { ReorderQuestionsButton } from '../shared/components/ReorderQuestionsButton';
import { ReorderQuestionsModalComponent } from '../shared/components/ReorderQuestionsModal/ReorderQuestionsModal';
import { SurveyActions } from '../shared/components/SurveyActions';
import { SurveyHeader } from '../shared/components/SurveyHeader';
import { SurveySettings } from '../shared/components/SurveySettings';
import { BasicSurveyInfo } from './components/BasicSurveyInfo';
import { NotificationOptions } from './components/NotificationOptions';
import { PreviewSatisfactionSurveyForm } from './components/PreviewSatisfactionSurveyForm';
import { ReviewRequestOptions } from './components/ReviewRequestOptions';
import { useSatisfactionFormInitialization, useSatisfactionSurveyMutations } from './hooks';
import { styleSheet } from './styles';
import { SatisfactionSurveyFormData, TabValues, validationSchema } from './utils';

const { useCallback } = React;

const DEFAULT_NAME = 'Untitled Survey';

interface IProps extends IImpersonationContextComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IEditSurveyComponent {
	BasicSurveyInfo: React.FC<{ survey: Api.ISatisfactionSurvey }>;
	ReviewRequestOptions: React.FC<IImpersonationContextComponentProps & { survey: Api.ISatisfactionSurvey }>;
	AdditionalQuestions: React.FC<{ isEdit: boolean; isClone: boolean }>;
	NotificationOptions: React.FC<IImpersonationContextComponentProps & { survey: Api.ISatisfactionSurvey }>;
}

const SatisfactionSurveyBase: React.FC<IProps> & Partial<IEditSurveyComponent> = observer(props => {
	const { className, styles = [], impersonationContext } = props;
	const userSession = useUserSession();
	const surveyPermissions = useSurveyPermissions();
	const params = useParams<{ id: string }>();

	const {
		surveyData: survey,
		isClone,
		isEdit,
		isLoading: surveyDataIsLoading,
	} = useClonableSurvey<Api.ISatisfactionSurvey>(params.id, impersonationContext);

	const [showEditSurveyName, setShowEditSurveyName] = React.useState(false);
	const [showReorderModal, setShowReorderModal] = React.useState(false);
	const [selectedTab, setSelectedTab] = React.useState<TabValues>(TabValues.Settings);
	const [isQRModalOpen, setIsQRModalOpen] = React.useState(false);
	const defaultValues = React.useMemo(() => {
		return {
			name: '',
			companyDisplayName: userSession.account.companyName || '',
			intro: 'How would you rate your overall experience with us?',
			reviewRequestSettings: {
				isEnabled: false,
				ratings: [4, 5],
				reviewRequest:
					'Thank you so much for your rating. Would you be so generous as to leave us a review? It would really mean the world to us.',
				reviewType: '',
				reviewUrl: '',
			},
			indefinitely: true,
			startDate: new Date().toISOString(),
			expirationDate: '',
			customFormIsDisabled: false,
			customForm: {
				name: '',
				fields: [] as Api.IFormField<string>[],
			},
			customFormMetaData: [] as Api.CustomFormMetaData[],
			notificationSettings: {
				isEnabled: false,
				additionalRecipients: [] as string[],
				ratings: [1, 2, 3, 4, 5],
			},
			tagGenerationSettings: {
				tagOnCompletion: false,
				completionTag: '',
				customFormFieldTagSettings: [] as Api.ICustomFormFieldTagSettings[],
				effectiveCompletionTag: undefined as Api.IAccountTag | undefined,
			},
		};
	}, [userSession.account.companyName]);
	const methods = useForm<SatisfactionSurveyFormData>({
		defaultValues: {
			...defaultValues,
			customFormMetaData: [] as Api.CustomFormMetaData[],
		},
		resolver: yupResolver(validationSchema),
	});
	const { handleSubmit, getValues, setValue, watch, formState, reset } = methods;
	const formValues = watch();

	// Use modern mutation hook pattern (follows EventRegistrationSurvey and CustomSurvey)
	const { createSurveyMutation, updateSurveyMutation, reorderSurveyFieldsMutation } = useSatisfactionSurveyMutations({
		impersonationContext,
		getValues,
		reset,
		surveyData: survey,
		isClone,
		onReorderSuccess: reorderedFields => {
			// Get current form fields to preserve their complete data (including options)
			const currentFields = getValues().customForm?.fields || [];

			// Create a map of current fields by ID for quick lookup
			const currentFieldsMap = new Map(currentFields.map(field => [field.id, field]));

			// Reorder the complete field objects based on the server response order
			const reorderedCompleteFields = reorderedFields
				.map(serverField => currentFieldsMap.get(serverField.id))
				.filter(Boolean) as Api.IFormField<string>[];

			setValue('customForm.fields', reorderedCompleteFields);
		},
	});

	// Use initialization hook to populate form data
	useSatisfactionFormInitialization({
		surveyData: survey,
		isClone,
		setValue,
	});

	const isArchived = Boolean(survey?.archivedDate);

	// Track if customForm fields have meaningful changes by comparing specific properties
	const customFormFieldsHaveChanges = React.useMemo(
		() => hasCustomFormChanges(survey?.customForm?.fields, formValues.customForm?.fields),
		[survey?.customForm?.fields, formValues.customForm?.fields]
	);

	// Track if customFormMetaData has changes (conditional question settings)
	const customFormMetaDataHasChanges = React.useMemo(() => {
		const originalMetaData = survey?.customFormMetaData || [];
		const currentMetaData = formValues.customFormMetaData || [];

		// If lengths differ, there are changes
		if (originalMetaData.length !== currentMetaData.length) {
			return true;
		}

		// Compare each metadata entry
		return originalMetaData.some((originalMeta, index) => {
			const currentMeta = currentMetaData[index];
			if (!currentMeta) return true;

			// Compare conditional settings
			if (originalMeta.isConditional !== currentMeta.isConditional) return true;
			if (originalMeta.fieldId !== currentMeta.fieldId) return true;

			// Compare ratings array
			const originalRatings = originalMeta.ratings || [];
			const currentRatings = currentMeta.ratings || [];

			if (originalRatings.length !== currentRatings.length) return true;

			return originalRatings.some((rating, ratingIndex) => rating !== currentRatings[ratingIndex]);
		});
	}, [survey?.customFormMetaData, formValues.customFormMetaData]);

	// Track if we just finished loading to ignore false positives from HtmlEditor initialization
	const [isInitializing, setIsInitializing] = React.useState(true);
	const hasResetForSurvey = React.useRef<string | null>(null);

	React.useEffect(() => {
		const surveyId = survey?.id;
		if (!surveyDataIsLoading && survey && hasResetForSurvey.current !== surveyId) {
			// Mark that we're handling this survey to prevent duplicate resets
			hasResetForSurvey.current = surveyId;

			// Give HtmlEditor time to finish its initialization before considering changes
			// 1000ms timeout is based on manual testing showing HtmlEditor (TinyMCE) typically
			// initializes within 500-800ms. The 1000ms value provides headroom for slower machines.
			// Future improvement: Use HtmlEditor's onLoad callback instead of timeout (requires refactoring form components)
			const timer = setTimeout(() => {
				// Reset the form with current values to clear dirty state without changing values
				// This updates both the values and default values, marking everything as clean
				const currentValues = getValues();
				reset(currentValues);
				setIsInitializing(false);
			}, 1000);
			return () => clearTimeout(timer);
		}
	}, [surveyDataIsLoading, survey, getValues, reset]);

	// Track unsaved changes
	const hasUnsavedChanges = useUnsavedChanges({
		surveyType: 'satisfaction',
		isEdit,
		isClone,
		isInitializing,
		formState,
		additionalConditions: [customFormFieldsHaveChanges, customFormMetaDataHasChanges],
	});

	React.useEffect(() => {
		if (formState.errors?.name?.message) {
			setShowEditSurveyName(true);
		}
	}, [formState.errors]);

	const handleSave = () => {
		const values = getValues();

		const payload: Api.ISatisfactionSurvey = {
			_type: 'SatisfactionSurvey',
			companyDisplayName: values.companyDisplayName,
			intro: values.intro,
			reviewRequestSettings: {
				isEnabled: values.reviewRequestSettings.isEnabled,
				ratings: values.reviewRequestSettings.ratings,
				reviewRequest: values.reviewRequestSettings.reviewRequest,
				reviewType: values.reviewRequestSettings.reviewType,
				reviewUrl: values.reviewRequestSettings.reviewUrl,
			},
			name: values.name,
			startDate: values.startDate,
			expirationDate: values.expirationDate,
			customFormIsDisabled: false,
			customForm: isClone
				? {
						// When cloning, don't spread original survey data - use only form values
						name: values.name,
						fields: values.customForm.fields,
					}
				: {
						// When editing existing survey, merge with original data
						...survey?.customForm,
						name: values.name,
						fields: values.customForm.fields,
					},
			customFormMetaData: values.customFormMetaData,
			notificationSettings: {
				_type: 'SatisfactionSurveyNotificationSettings' as const,
				isEnabled: values.notificationSettings?.isEnabled ?? false,
				ratings: values.notificationSettings?.ratings ?? [1, 2, 3, 4, 5],
				additionalRecipients: (values.notificationSettings?.additionalRecipients ?? []).filter(
					recipient => recipient && recipient.trim() !== ''
				),
			},
			tagGenerationSettings: {
				tagOnCompletion: values.tagGenerationSettings?.tagOnCompletion || false,
				completionTag: values.tagGenerationSettings?.completionTag || '',
				customFormFieldTagSettings: values.tagGenerationSettings?.customFormFieldTagSettings || [],
				effectiveCompletionTag: values.tagGenerationSettings?.effectiveCompletionTag,
			},
		};
		if (
			payload.startDate &&
			payload.expirationDate &&
			moment(payload.startDate).isSame(new Date(payload.expirationDate), 'day')
		) {
			payload.startDate = moment(payload.startDate).startOf('day').toISOString();
			payload.expirationDate = moment(payload.expirationDate).endOf('day').toISOString();
		} else if (payload.expirationDate && !moment(payload.startDate).isSame(new Date(payload.expirationDate), 'day')) {
			payload.expirationDate = moment(payload.expirationDate).endOf('day').toISOString();
		}
		const validCustomFormFields = payload.customForm.fields.filter(x => Boolean(x.label));

		if (!validCustomFormFields.length) {
			payload.customForm = null;
		}

		// Use modern single API call pattern
		if (isEdit) {
			// Update survey - single API call handles everything
			updateSurveyMutation.mutate({
				survey: isClone
					? {
							// When cloning, don't spread original survey data - use only payload
							...payload,
						}
					: {
							// When editing existing survey, merge with original data
							...survey,
							...payload,
						},
			});
		} else {
			// Create survey - single API call handles everything
			createSurveyMutation.mutate({
				survey: payload,
			});
		}
	};

	const onCopyLinkClicked = () => {
		if (copyToClipboard(survey?.anonymousLink)) {
			toast.success('Link copied to clipboard');
			return;
		}
		toast.error('Error copying link to clipboard');
	};

	const onQRCodeClick = () => {
		setIsQRModalOpen(true);
	};

	const handleQRModalClose = () => {
		setIsQRModalOpen(false);
	};

	const handleReorderQuestions = () => {
		setShowReorderModal(true);
	};

	const handleReorderModalClose = useCallback(
		(result?: { reorderedQuestions?: any }, cancel?: boolean) => {
			setShowReorderModal(false);
			if (!cancel && result?.reorderedQuestions) {
				reorderSurveyFieldsMutation.mutate({
					surveyId: survey.id,
					fieldIds: result.reorderedQuestions.map((field: Api.IFormField<string>) => field.id),
				});
			}
		},
		[reorderSurveyFieldsMutation, survey]
	);

	const isLoading = React.useMemo(
		() => (surveyDataIsLoading && isEdit) || createSurveyMutation.isPending || updateSurveyMutation.isPending,
		[surveyDataIsLoading, isEdit, createSurveyMutation.isPending, updateSurveyMutation.isPending]
	);
	const settingsErrors = Object.keys(formState.errors).some(x =>
		['intro', 'name', 'reviewRequestSettings', 'notificationSettings'].includes(x)
	);

	const customFormErrors = Object.keys(formState.errors).some(x => x.startsWith('customForm'));

	const questions = formValues?.customForm?.fields || [];
	const hasMultipleQuestions = questions.length > 1;

	const breadcrumbPageTitle = useCallback(() => {
		if (isClone) {
			return 'Clone Survey';
		}
		return params.id ? (survey?.name ?? 'Edit Survey') : 'New Survey';
	}, [params.id, survey?.name, isClone]);

	return (
		<FormProvider {...methods}>
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/surveys/satisfaction'>Surveys</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>{breadcrumbPageTitle()}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
			/>
			<form
				className={`${css(styleSheet.satisfactionSurveyContainer, ...styles)} edit-survey ${className || ''}`}
				onSubmit={handleSubmit(handleSave)}
			>
				<Page styles={[styleSheet.page]}>
					<MultiContainerHeader fullscreenHeader={`${isEdit ? (isArchived ? 'View' : 'Edit') : 'New'} Survey`} />
					<FabContext appearance={{ hidden: true }} />
					<header className={css(styleSheet.header)}>
						<SurveyHeader
							surveyName={formValues.name || DEFAULT_NAME}
							showEditName={showEditSurveyName}
							onEditToggle={() => setShowEditSurveyName(!showEditSurveyName)}
							onNameChange={name => setValue('name', name)}
							validationError={formState.errors?.name?.message}
							isEdit={isEdit}
						/>
						<SurveyActions
							onCopyLink={onCopyLinkClicked}
							isLoading={isLoading}
							isSaveDisabled={showEditSurveyName}
							showCopyLink={!!survey?.id}
							surveyLink={survey?.anonymousLink}
							onQRCodeClick={onQRCodeClick}
							showViewReport={!!survey?.id && surveyPermissions.canViewReportingSatisfactionSurvey()}
							surveyId={survey?.id}
							surveyType='SatisfactionSurvey'
						/>
					</header>
					<UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges && !surveyDataIsLoading} />
					<TabRoot
						className={`tab-view ${css(styleSheet.tabViewContainer)}`}
						value={selectedTab}
						onValueChange={tValue => setSelectedTab(tValue as TabValues)}
					>
						<TabList styles={[styleSheet.tabList]}>
							<IconTabTrigger
								value={TabValues.Settings}
								icon={<TabSettingsIcon fillColor='currentColor' />}
								styles={settingsErrors ? [styleSheet.tabTextError] : []}
							>
								Settings
							</IconTabTrigger>
							<IconTabTrigger
								value={TabValues.Questions}
								styles={customFormErrors ? [styleSheet.tabTextError] : []}
								icon={<ListOfQuestionsIcon fillColor='currentColor' />}
							>
								Questions
							</IconTabTrigger>
							{survey?.anonymousLink ? (
								<IconTabTrigger value={TabValues.Preview} icon={<EyeIcon fillColor='currentColor' />}>
									Preview
								</IconTabTrigger>
							) : null}
						</TabList>
						<TabContent value={TabValues.Settings} className={css(styleSheet.tabContent, styleSheet.newTabContent)}>
							<SurveySettings
								tabs={[
									{
										title: 'Basic Info',
										key: 'basic-info',
										content: <SatisfactionSurvey.BasicSurveyInfo survey={survey} />,
									},
									{
										title: 'Google Review',
										key: 'google-review',
										content: <SatisfactionSurvey.ReviewRequestOptions survey={survey} />,
									},
									{
										title: 'Notification Options',
										key: 'notification-options',
										content: <SatisfactionSurvey.NotificationOptions survey={survey} />,
									},
								]}
							/>
						</TabContent>
						<TabContent value={TabValues.Questions} className={css(styleSheet.tabContent)}>
							<div className={css(styleSheet.bodyBackground)} />

							<div className={css(styleSheet.formContainer)}>
								<ReorderQuestionsButton
									onReorderQuestions={handleReorderQuestions}
									hasMultipleQuestions={hasMultipleQuestions}
									isLoading={isLoading}
								/>
								<div className={css(styleSheet.additionalQuestionsContainer)}>
									<SatisfactionSurvey.AdditionalQuestions isEdit={isEdit} isClone={isClone} />
								</div>
							</div>
						</TabContent>
						{survey?.anonymousLink ? (
							<TabContent value={TabValues.Preview} className={css(styleSheet.tabContent)}>
								<div className={css(styleSheet.formContainer)}>
									<PreviewSatisfactionSurveyForm survey={survey} />
								</div>
							</TabContent>
						) : null}
					</TabRoot>
				</Page>
			</form>
			<AppDrawer>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
				</AppDrawerToolbar>
			</AppDrawer>
			<ReorderQuestionsModalComponent
				modalProps={{
					isOpen: showReorderModal,
					onRequestClose: handleReorderModalClose,
				}}
				questions={questions}
				surveyId={params.id || ''}
				impersonationContext={impersonationContext}
			/>
			<QRCodeModal
				modalProps={{
					isOpen: isQRModalOpen,
					onRequestClose: handleQRModalClose,
				}}
				url={survey?.anonymousLink || ''}
				title='QR Code'
				filename={`survey-qr-${survey?.id || 'code'}`}
			/>
		</FormProvider>
	);
});

export const SatisfactionSurvey = inject(ImpersonationContextKey)(SatisfactionSurveyBase);

SatisfactionSurvey.BasicSurveyInfo = BasicSurveyInfo;
SatisfactionSurvey.ReviewRequestOptions = ReviewRequestOptions;
SatisfactionSurvey.AdditionalQuestions = AdditionalQuestions;
SatisfactionSurvey.NotificationOptions = NotificationOptions;
